/**
 * usePiaStyles Hook
 *
 * React hook providing PIA (Physical Infrastructure Access) styling functions.
 * Replaces the styling functionality from legacy pia.js.
 *
 * Functions provided:
 * - PIAStyle: Get style for duct features based on RAG status
 * - PIALineStyle: Apply text labels and hover effects to line features
 * - PIAIcons: Get appropriate icon for point features
 * - fetchStructures: Fetch and cache multiple structures
 * - fetchNOI: Fetch NOI data as GeoJSON layer
 */
import { useCallback } from 'react';
// RAG status colours
const ragColor = (rag) => {
    if (rag === 'R')
        return '#FF0000';
    if (rag === 'A')
        return '#FFC200';
    if (rag === 'G')
        return '#00FF00';
    return '#000000';
};
// BFT status colours
const bftColor = (status) => {
    if (status === 'Roped')
        return '#50BFE6';
    if (status === 'Planned')
        return '#9C51B6';
    return '#FF8833';
};
/**
 * Get style for a PIA duct feature
 */
export function getPIAStyle(feature, forceBlue = false) {
    const props = feature.properties;
    return {
        color: props.BFT_STATUS
            ? bftColor(props.BFT_STATUS)
            : (forceBlue ? '#0066ff' : ragColor(props.RAG_STATUS)),
        weight: Math.min((props.NUMBER_OF_WAYS || 1) + 1, 6)
    };
}
/**
 * Apply text labels and hover effects to a line feature layer
 */
export function applyPIALineStyle(feature, layer, forceBlue = false) {
    const props = feature.properties;
    // Skip point features
    if (feature.geometry.type === 'Point') {
        return;
    }
    // Apply text label
    if (typeof layer.setText === 'function') {
        if (props.BFT_STATUS === 'Installed') {
            layer.setText(`    ${Math.round(props.MEASURED_LENGTH || 0)}m ${props.NETWORK === 'CFN' ? 'CFN' : props.EXCHANGE_1141_CODE || ''}:${props.SECTION_NUMBER || (props.ID ? `DB${props.ID}` : '')} - ${props.BFT_OUR_REF || ''}  `, { repeat: true, offset: -3 });
        }
        else {
            layer.setText(`  ${props.BFT_OUR_REF || ''} ${Math.round(props.MEASURED_LENGTH || 0)} m ( ${props.NUMBER_OF_WAYS || ''} ) ${props.SECTION_NUMBER} - ${props.RAG_STATUS || ''}    `, { repeat: true, offset: -3 });
        }
    }
    // Apply hover effects
    layer.on('mouseover', function () {
        this.setStyle({
            color: '#0000ff',
            weight: getPIAStyle(feature, forceBlue).weight + 2
        });
    });
    layer.on('mouseout', function () {
        this.setStyle(getPIAStyle(feature, forceBlue));
    });
}
// Icon definitions - these are static and created once
let iconsInitialized = false;
let icons = {};
/**
 * Initialize Leaflet icons for PIA structures
 */
function initializeIcons() {
    if (iconsInitialized)
        return;
    const L = window.L;
    if (!L)
        return;
    icons = {
        'CAB': L.icon({
            iconUrl: '/cabinet_icon.png',
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, 0]
        }),
        'CHANGE': L.icon({
            iconUrl: '/change_of_state.png',
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, 0]
        }),
        'SPLIT': L.icon({
            iconUrl: '/split_coupling.png',
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, 0]
        }),
        'DPOLE': L.icon({
            iconUrl: '/red_pole_centred.png',
            iconSize: [64, 64],
            iconAnchor: [32, 32],
            popupAnchor: [0, 0]
        }),
        'P': L.icon({
            iconUrl: '/pole_centred.png',
            iconSize: [64, 64],
            iconAnchor: [32, 32],
            popupAnchor: [0, 0]
        }),
        'M': L.icon({
            iconUrl: '/manhole.png',
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            popupAnchor: [0, 0]
        }),
        'F': L.icon({
            iconUrl: '/chamber.png',
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            popupAnchor: [0, 0]
        }),
        'C': L.icon({
            iconUrl: '/carriageway.png',
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            popupAnchor: [0, 0]
        }),
    };
    // Add numbered variants for chambers/carriageways
    const chamberSizes = [2, 3, 4, 6, 9, 10, 11, 23, 26];
    const carriagewaySize = [1, 3, 4, 6, 9, 10, 11, 12, 14];
    chamberSizes.forEach(num => {
        icons[`F${num}`] = L.icon({
            iconUrl: `/chamber_${num}.png`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            popupAnchor: [0, 0]
        });
    });
    carriagewaySize.forEach(num => {
        icons[`C${num}`] = L.icon({
            iconUrl: `/carriageway_${num}.png`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            popupAnchor: [0, 0]
        });
    });
    // Aliases
    const aliases = {
        'F2': ['F102'],
        'F4': ['F104'],
        'F6': ['F106'],
        'F10': ['F110'],
    };
    Object.entries(aliases).forEach(([icon, aliasList]) => {
        aliasList.forEach(alias => {
            icons[alias] = icons[icon];
        });
    });
    iconsInitialized = true;
}
/**
 * Get appropriate icon marker for a PIA point feature
 */
export function getPIAIcon(feature, latlng) {
    const L = window.L;
    if (!L)
        return null;
    // Initialize icons if needed
    initializeIcons();
    const props = feature.properties;
    // Manhole
    if (props.TYPE_NAME === 'MANHOLE') {
        return L.marker(latlng, { icon: icons['M'] });
    }
    // Cabinet
    if (props.OBJECT_CLASS === 'CAB') {
        return L.marker(latlng, { icon: icons['CAB'] });
    }
    // Change of state
    if (props.TYPE_NAME === 'CHANGE OF STATE') {
        return L.marker(latlng, { icon: icons['CHANGE'] });
    }
    // Split coupling
    if (props.TYPE_NAME === 'SPLIT COUPLING') {
        return L.marker(latlng, { icon: icons['SPLIT'] });
    }
    // Pole
    if (props.OBJECT_CLASS === 'POLE') {
        if (props.RESULT === 'DEC') {
            return L.marker(latlng, { icon: icons['DPOLE'] });
        }
        return L.marker(latlng, { icon: icons['P'] });
    }
    // Joint box
    const plantItem = props.PLANT_ITEM;
    if (plantItem) {
        const parts = plantItem.match(/^J.([FC])X?\s*(\d*)[ABC]?$/) || plantItem.match(/^JB\s*(\d+)$/);
        if (parts) {
            const [, FC, num] = parts.length === 3 ? parts : [parts[0], 'F', parts[1]];
            if (num && icons[FC + num]) {
                return L.marker(latlng, { icon: icons[FC + num] });
            }
            return L.marker(latlng, { icon: icons[FC] || icons['F'] });
        }
    }
    // Default to chamber icon
    return L.marker(latlng, { icon: icons['F'] });
}
/**
 * Fetch multiple structures and add to cache
 */
export async function fetchStructures(features) {
    const CFN = window.CFN;
    const L = window.L;
    if (!CFN?.structureCache || !L)
        return;
    // Get unique endpoints from features
    const endpointSet = new Set();
    features.forEach(f => {
        if (f.geometry.type !== 'Point') {
            const coords = f.geometry.coordinates;
            endpointSet.add(JSON.stringify(coords[0]));
            endpointSet.add(JSON.stringify(coords[coords.length - 1]));
        }
    });
    // Filter out already cached endpoints
    const endpoints = [...endpointSet]
        .filter(str => !CFN.structureCache.has(str))
        .map(point => JSON.parse(point));
    if (endpoints.length === 0)
        return;
    try {
        const response = await fetch('/structure/fetchMultiple', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(endpoints)
        });
        const structures = await response.json();
        structures.forEach((feature) => {
            const coords = feature.geometry.coordinates;
            const point = coords.map(num => num.toFixed(8));
            const key = JSON.stringify(new L.LatLng(point[1], point[0]));
            CFN.structureCache.set(key, feature);
        });
    }
    catch (error) {
        console.error('fetchStructures: Error:', error);
    }
}
/**
 * Fetch NOI GeoJSON data
 */
export function fetchNOI(noiRef, route, map, callback) {
    const L = window.L;
    const CFN = window.CFN;
    if (!L?.GeoJSON?.AJAX || !CFN)
        return;
    const path = `/pia_noi/${noiRef}/${route ? route + '/' : ''}geojson`;
    const layer = new L.GeoJSON.AJAX(path, {
        style: (feature) => getPIAStyle(feature, map.hasLayer(CFN.getCachedLayer('ConduitLayer'))),
        zIndex: 100,
        middleware: (data) => {
            for (const feature of data.features) {
                if (feature.geometry.type === 'Point')
                    continue;
                const coords = feature.geometry.coordinates;
                const flip = coords[0][0] > coords[coords.length - 1][0];
                if (flip)
                    coords.reverse();
            }
            return data;
        },
        pointToLayer: getPIAIcon,
        onEachFeature: (feature, layer) => {
            applyPIALineStyle(feature, layer, map.hasLayer(CFN.getCachedLayer('ConduitLayer')));
        }
    });
    callback(layer);
}
/**
 * Hook for PIA styling (for components that need reactive access)
 */
export function usePiaStyles() {
    const getStyle = useCallback((feature, forceBlue = false) => {
        return getPIAStyle(feature, forceBlue);
    }, []);
    const applyLineStyle = useCallback((feature, layer, forceBlue = false) => {
        applyPIALineStyle(feature, layer, forceBlue);
    }, []);
    const getIcon = useCallback((feature, latlng) => {
        return getPIAIcon(feature, latlng);
    }, []);
    return {
        getStyle,
        applyLineStyle,
        getIcon,
        fetchStructures,
        fetchNOI,
    };
}
/**
 * Initialize CFN PIA functions for backward compatibility with legacy code.
 * Call this once during app initialization.
 */
export function initializePiaStyles() {
    if (typeof window !== 'undefined') {
        const CFN = window.CFN || {};
        window.CFN = CFN;
        // Wait for Leaflet to be available before initializing
        const initWhenReady = () => {
            const L = window.L;
            if (!L) {
                setTimeout(initWhenReady, 100);
                return;
            }
            initializeIcons();
            CFN.PIAStyle = getPIAStyle;
            CFN.PIALineStyle = applyPIALineStyle;
            CFN.PIAIcons = getPIAIcon;
            CFN.fetchStructures = fetchStructures;
            CFN.fetchNOI = fetchNOI;
            console.log('usePiaStyles: Initialized CFN.PIAStyle, CFN.PIALineStyle, CFN.PIAIcons, CFN.fetchStructures, CFN.fetchNOI');
        };
        initWhenReady();
    }
}
export default usePiaStyles;
